import * as uuid        from 'uuid';

import React            from 'react';
import { useParams }    from 'react-router-dom';

import * as AgencyType  from '../shared/types/Agency';
import * as UserType    from '../shared/types/User';
import * as AEVApplicationType  from '../shared/types/AEVApplication';
import * as USAddress   from '../shared/utils/USAddress'
import dayjs            from '../shared/utils/day-timezone';

import Alert            from '../utils/Alert';
import * as MenuItem    from '../components/MenuItem';
import intelitransit    from '../utils/InteliTransit';
import getApiPromise    from '../utils/getApiPromise';

interface Props {
}

export const AEVApplication : React.FC<Props> = props => {

    const params        = useParams<{id:string}>();
    const [
        loading,
        setLoading
    ] = React.useState<boolean>(true);
    const [
        existingApplication,
        setExistingApplication
    ] = React.useState<AEVApplicationType.Application|undefined>(undefined);
    const [
        application,
        setApplication
    ] = React.useState<AEVApplicationType.Application>({
        _id                 : (params.id||uuid.v4()),
        createdAt           : Date.now(),
        updatedAt           : Date.now(),
        agencyId            : intelitransit.agency._id,
        applicant           : undefined,
        questionnaire       : {
        }
    });
    const [
        questionNdxStack,
        setQuestionNdxStack
    ] = React.useState<string[]>(["1"]);
    const [
        submittedAt,
        setSubmittedAt
    ] = React.useState<number>(0);

    React.useEffect(() => {
        getApiPromise<AEVApplicationType.Dehydrated>('/api/public/application','GET',{},{appId:params.id})
            .then( app => {
                if( !app || app.err )
                    throw Error(app?.err||'app is empty');
                setExistingApplication(AEVApplicationType.hydrate(app));
            })
            .catch( err => {
                // That's OK. We do not want to find it.
                console.log(`Cannot get application (${err.message})`,5000);
            })
            .finally(()=>{
                setLoading(false);
            });
    },[params.id]);

    let   alert         = new Alert();
    const questions     = {
        "1" : {
            type    : "select",
            text    : "Do you use a mobility aid?",
            options : {
                "None"                      : "-1",
                "Cane / Walker"             : "-1",
                "Manual Wheelchair"         : "2",
                "Power Wheelchair"          : "2",
                "Scooter"                   : "2" ,
                "Other"                     : "-1"
            }
        },
        "2" : {
            type    : "select",
            text    : "Are you aware that fixed-route buses are equipped with?",
            options : {
                "Wheelchair lift or ramps" : "3",
                "Securement areas"         : "3",
                "Priority seating"         : "3",
                "Yes"                      : "3",
                "No"                       : "3"
            }
        },
        "3" : {
            type    : "select",
            text    : "Are you able to board and exit a fixed-route bus using a ramp or lift independently or with minimal assistance?",
            options : {
                "Yes, independently"        : "4",    // to environmental barriers
                "Yes, with assistance"      : "4",    // conditional eligibility
                "No"                        : "4"     // ADA category 1 eligibility validation
            }
        },
        "4" : {
            type    : "select",
            text    : "Can you safely remain seated or secured in your mobility device during transit?",
            options : {
                "Yes"       : "5",
                "No"        : "5",
                "Not sure"  : "5"
            }
        },
        "5" : {
            type    : "select",
            text    : "Can you travel to and from a bus stop (typically 1/4-3/4 mile)?",
            options : {
                "Yes"               : "6",
                "Only under certain conditions" : "5a",
                "No"                : "6"
            }
        },
        "5a" : {
            type    : "select",
            text    : "What conditions prevent you?",
            options : {
                "No sidewalks"      : "6",
                "Steep hills"       : "6",
                "Weather (heat, rain, ice)" : "6",
                "Distance too far"  : "6",
                "Safety concerns"   : "6",
                "Fatigue"           : "6",
                "Other"             : "6"
            }
        },
        "6" : {
            type    : "select",
            text    : "Are there conditions where your disability prevents you from using fixed-route transit?",
            options : {
                "Extreme heat/cold"             : "7",
                "Rain or wet surfaces"          : "7",
                "Darkness / low visibility"     : "7",
                "High traffic areas"            : "7",
                "Crowded buses"                 : "7",
                "Variable health condition (good days/bad days)" : "7"
            }
        },
        "7" : {
            type    : "select",
            text    : `Are you able to Understand routes and schedules?`,
            options : {
                "Yes"           : "8",
                "No"            : "8",
                "Sometimes"     : "8"
            },
            node : `Are you able to:<ul>
                        <li>Understand routes and schedules?</li>
                        <li>Recognize stops and landmarks?</li>
                        <li>Ask for help if needed?</li>
                    </ul>`
        },
        // TODO:
        // Break these down to 2 separate questions with Yes/No/Somtimes answers
        // The quesiton should be part of the the dropbox.        
        "8" : {
            type    : "select",
            text    : "Can you wait at a bus stop for up to 30 minutes?",
            options : {
                "Yes"                                       : "9",
                "No"                                        : "9",
                "Only sometimes"                            : "9",
            }
        },
        "9" : {
            type    : "select",
            text    : "Transfer between buses if needed?",
            options : {
                "Yes"                                       : "10",
                "No"                                        : "10",
                "Only sometimes"                            : "10",
            }
        },        
        "10" : {
            type    : "select",
            text    : "Have you used fixed-route transit in the past 6 months?",
            options : {
                "Yes regularly"                             : "11",
                "Yes occasionally"                          : "11",
                "No"                                        : "11"
            }
        },
        // 8/7/2026: Rashida requested to remove this question completely.
        /*
        "10" : {
            text    : "Does a medical or rehabilitation professional support your need for ADA paratransit?",
            options : {
                "Yes"                       : "11",
                "No"                        : "11",
                "Pending"                   : "11"
            }
        }
        */
    } as Record<string,AgencyType.Question>;

    const renderExistingApplication = ( 
        app:AEVApplicationType.Hydrated,
        thead? : React.JSX.Element,
        tfoot? : React.JSX.Element
    ) : React.ReactElement<any, string | React.JSXElementConstructor<any>> => {
        const getTBody = ( data:Record<string,string|React.JSX.Element> ) => {
            return (
                <tbody>
                    {Object.entries(data).map( ([k,v],ndx) => {
                        return (
                            <tr key={ndx}>
                                <td align='left' valign='top'>
                                    <strong>{k}</strong>
                                </td>
                                <td align='left' valign='top'>
                                    {v}
                                </td>
                            </tr>
                        );
                    })}
                </tbody>
            );
        }
        const getReviewData = ( review:AEVApplicationType.Review ) => {
            return {
                at              : dayjs(app.createdAt).tz(intelitransit.agency.timeZone).format(intelitransit.timeFormat),
                // TODO:
                // Replace with clerk name?
                clerkId         : review.clerkId,
                decision        : review.decision
            };
        }
        const getApplicantData = ( applicant:AEVApplicationType.Applicant ) => {
            return {
                "First Name"    : applicant.fname,
                "Last Name"     : applicant.lname,
                "Mail Address"  : applicant.mailAddress,
                "Home Address"  : (applicant.homeAddress||''),
                "D.O.B"         : dayjs(applicant.dob).tz(intelitransit.agency.timeZone).format("MM/DD/YYYY"),
                "Email Address" : applicant.emailAddress,
                "Phone Number"  : applicant.phoneNumber
            }
        };
        const getApplicationData = ( app:AEVApplicationType.Hydrated ) => {
            return {
                "Created"           : dayjs(app.createdAt).tz(intelitransit.agency.timeZone).format(intelitransit.timeFormat),
                "Updated"           : dayjs(app.updatedAt).tz(intelitransit.agency.timeZone).format(intelitransit.timeFormat),
                "Applicant"         : (app.applicant ? (<table>{getTBody(getApplicantData(app.applicant))}</table>) : "n/a"),
                "Questionnaire"     : (<table>{getTBody(app.questionnaire)}</table>),
                "Review"            : (app.review ? (<table>{getTBody(getReviewData(app.review))}</table>) : "n/a"),
            }
        };
        return (
            <table width="100%" style={{padding:"50px"}}>
            {thead && (<thead>{thead}</thead>)}
            {getTBody(getApplicationData(app))}
            {tfoot && (<tfoot>{tfoot}</tfoot>)}
            </table>
        );
    }
    const renderNewApplication = () => {
        const renderApplicant = () : React.ReactElement<any, string | React.JSXElementConstructor<any>> => {
            return (
                <table width="100%" style={{padding:"50px"}}>
                <tbody>
                    <tr>
                        <td align="left" width="30%">
                            <strong>First Name</strong>:
                        </td>
                        <td align='center' valign='top'>
                            <input type="text" className="input-theme input-theme-min" placeholder="First Name" id="fname" size={15} maxLength={20}/>
                        </td>
                    </tr>
                    <tr>
                        <td align="left" width="30%">
                            <strong>Last Name</strong>:
                        </td>
                        <td align='center' valign='top'>
                            <input type="text" className="input-theme input-theme-min" placeholder="Last Name" id="lname" size={15} maxLength={20}/>
                        </td>
                    </tr>
                    <tr>
                        <td align="left" width="30%">
                            <strong>Mail Address</strong>:
                        </td>
                        <td align='center' valign='top'>
                            <input type="text" className="input-theme input-theme-min" placeholder="Street, City, ST, ZIP" id="mailAddress" size={15} maxLength={200}/>
                        </td>
                    </tr>
                    <tr>
                        <td align="left" width="30%">
                            <strong>Home Address</strong>*:
                        </td>
                        <td align='center' valign='top'>
                            <input type="text" className="input-theme input-theme-min" placeholder="Street, City, ST, ZIP (optional)" id="homeAddress" size={15} maxLength={200}/>
                        </td>
                    </tr>
                    <tr>
                        <td align="left" width="30%">
                            <strong>D.O.B.</strong>:
                        </td>
                        <td align='center' valign='top'>
                            <input type="text" className="input-theme input-theme-min" placeholder="mm/dd/yyyy" id="dob" size={15} maxLength={20}/>
                        </td>
                    </tr>
                    <tr>
                        <td align="left" width="30%">
                            <strong>Email Address</strong>:
                        </td>
                        <td align='center' valign='top'>
                            <input type="text" className="input-theme input-theme-min" placeholder="me@me.com" id="emailAddress" size={15} maxLength={80}/>
                        </td>
                    </tr>
                    <tr>
                        <td align="left" width="30%">
                            <strong>Phone Number</strong>:
                        </td>
                        <td align='center' valign='top'>
                            <input type="text" className="input-theme input-theme-min" placeholder="123-4567890" id="phoneNumber" size={15} maxLength={20}/>
                        </td>
                    </tr>
                    <tr>
                        <td align="center" colSpan={2}>
                            <button className="btn btn-theme btn-min" onClick={ e => {
                                try {
                                    const dummyApplication = {} as AEVApplicationType.Application;
                                    const ddmmyyyRE = /^(?<dd>\d{1,2})\/(?<mm>\d{1,2})\/(?<yyyy>\d{4})$/;
                                    const isApplicantValid = ( applicant:Record<string,string> ) => {
                                        if( !applicant )
                                            throw Error(`Applicant is empty`);
                                        if( !applicant.fname )
                                            throw Error(`First name is required`);
                                        if( !applicant.lname )
                                            throw Error(`Last name is required`);
                                        try {
                                            USAddress.match(applicant.mailAddress,true);
                                        }
                                        catch( err ) {
                                            throw Error(`Mail address is invalid (${(err as Error).message})`);
                                        }
                                        if( applicant.homeAddress ) {
                                            try {
                                                USAddress.match(applicant.homeAddress,true)
                                            }
                                            catch( err ) {
                                                throw Error(`Home address is invalid (${(err as Error).message})`);
                                            }
                                        }
                                        const dobMatches = applicant.dob.match(ddmmyyyRE);
                                        if( !dobMatches || (Number(dobMatches.groups?.yyyy||Infinity)>(dayjs().year()-5)) )
                                            throw Error(`Date of birth is invalid`);
                                        if( !UserType.emailRegexp.test(applicant.emailAddress) )
                                            throw Error(`Email Address is invalid`);
                                        if( !UserType.phoneRegexp.test(applicant.phoneNumber) )
                                            throw Error(`Phone Number is invalid`);
                                        return {
                                            ...applicant,
                                            dob : dayjs(applicant.dob).valueOf()
                                        } as (typeof dummyApplication.applicant);
                                    }
                                    // Do validation
                                    setApplication({
                                        ...application,
                                        applicant : isApplicantValid(
                                            [
                                                "fname",
                                                "lname",
                                                "mailAddress",
                                                "homeAddress",
                                                "dob",
                                                "emailAddress",
                                                "phoneNumber"
                                            ].reduce((acc,fname) => {
                                                acc[fname] = ((document.getElementById(fname) as HTMLInputElement)?.value.trim()||'');
                                                return acc;
                                            },{} as Record<string,string>)
                                        )
                                    });
                                }
                                catch( err ) {
                                    alert.set((err as Error).message,5000);
                                }
                            }}>Next To Questions</button>
                        </td>
                    </tr>
                </tbody>
                </table>
            );
        }
        const renderQuestionnaire = () : React.ReactElement<any, string | React.JSXElementConstructor<any>> => {
            const question      = intelitransit.agency.questionnaire[questionNdxStack.at(-1)||"1"];
            const renderSelectQuestion = ( selectQuestion:AgencyType.SelectQuestion, answer?:string ) => {
                console.log({application,answer,options:selectQuestion.options});
                return (<>
                    <select
                        id           = {String(Date.now())}
                        defaultValue = {answer||""} 
                        onChange     = { (e) => {
                            try {
                                const newQNdx = selectQuestion.options[e.target.value];
                                if( newQNdx===undefined )
                                    throw new Error(`Invalid answer "${e.target.value}"`);
                                setApplication({
                                    ...application,
                                    questionnaire : {
                                        ...application.questionnaire,
                                        [question.text] : e.target.value
                                    }
                                });
                                setQuestionNdxStack(prev => [...prev, newQNdx]);
                            }
                            catch( err ) {
                                alert.set((err as Error).message,3000);
                            }
                        }}
                    >
                        <option value="">Select an option</option>
                        {Object.entries(selectQuestion.options).map(([key,value],ndx) => (
                            <option 
                                key     = {key} 
                                value   = {key}
                                defaultChecked={answer===key}
                            >
                                {key}
                            </option>
                        ))}
                    </select>
                    <br/>&nbsp;
                    <button className="btn btn-theme btn-min" onClick={ (e) => {
                        if( questionNdxStack.length>1 ) {
                            setQuestionNdxStack( prev => {
                                const newStack = [...prev];
                                newStack.pop();
                                return newStack;
                            });
                        }
                        else {
                            setQuestionNdxStack(["1"]);
                            setApplication({
                                ...application,
                                applicant : undefined,
                            });
                        }
                    }}>Back</button>
                </>);
            } 
            const renderQuestion = ( question:AgencyType.Question, answer?:string ) => {
                if( question.type==="select" )
                    return renderSelectQuestion(question as AgencyType.SelectQuestion,answer);
                // TODO
                return (<strong>Do not know how to render a question of type {question.type}</strong>);
            }
            return (
                <table width="100%" style={{padding:"50px"}}>
                <tbody>
                        { question ? (
                            <tr>
                                <td align="left" valign="top" width="30%">
                                    <strong>Q{questionNdxStack.at(-1)}</strong>: {question.node ? <span dangerouslySetInnerHTML={{ __html: question.node }}/> : question.text}
                                </td>
                                <td align='center' valign='top'>
                                    {renderQuestion(question,application.questionnaire[question.text])}
                                </td>
                            </tr>
                        ) : (!submittedAt) ? (<>
                            <tr>
                                <td align="center" colSpan={2}>
                                    {renderExistingApplication(
                                        application,
                                        <tr>
                                            <td align='center' colSpan={2}>
                                                Please review your answers
                                            </td>
                                        </tr>
                                    )}
                                </td>
                            </tr>
                            <tr>
                                <td align='center' valign='top'>
                                    <button className="btn btn-theme btn-min" onClick={ (e) => {
                                        setQuestionNdxStack(["1"]);
                                        setSubmittedAt(0);
                                    }}>Start Over</button>
                                </td>
                                <td align='center' valign='top'>
                                    <button className="btn btn-theme btn-min" onClick={ e => {
                                        getApiPromise(`/api/public/application`,"POST",AEVApplicationType.dehydrate(application))
                                            .then( res => {
                                                alert.set(`Successfully submitted`,5000);
                                                setSubmittedAt(Date.now());
                                            })
                                            .catch( err => {
                                                alert.set(`Cannot submit application ${err.message}`,5000);
                                            })
                                    }}>Submit</button>
                                </td>                                
                            </tr>
                        </>) : (
                            <tr>
                                <td align="left" colSpan={2}>
                                    <p>Application Submitted Successfully</p>
                                    <p>Congratulations! Your ADA Paratransit application with <strong>{intelitransit.agency.name}</strong> has been submitted.</p>

                                    <p>Your Application Number: <strong>{application._id}</strong></p>

                                    <p>Please write down or save your application number for your records. An email confirmation with this number has been sent to rashida@email.com, along with instructions on how to check the status of your application.</p>

                                    <p>What Happens Next?
                                    <ul>
                                    <li><strong>{intelitransit.agency.name}</strong> has up to 21 days to review and make a determination on your application.</li>
                                    <li>You may hear from us sooner.</li>
                                    <li>If additional information is needed, our team will contact you by phone.</li>
                                    </ul>
                                    </p>
                                    <p>
                                    Once your application is assigned to an eligibility agent, you will receive a follow-up email confirming that it is under review.
                                    </p>

                                    <p>Need Help?</p>

                                    <p>If you have questions or need assistance, please contact us: <strong>{intelitransit.agency.phoneNumber}</strong></p>

                                    <p>Thank you, <strong>{intelitransit.agency.name} Team</strong></p>
                                    <br/>
                                </td>
                            </tr>
                        )}
                </tbody>
                </table>
            );
        }
        return application.applicant ? renderQuestionnaire() : renderApplicant();
    }
    return MenuItem.withMenuItem("ADA Eligibility Verification",(alert_) => {
        alert = alert_;
        return (
            <div className="content">
                <div style={{ textAlign: "center", overflowY:'auto' }}>
                { loading 
                    ? (<p>Loading...</p>) 
                    : ( existingApplication ? renderExistingApplication(
                        existingApplication,
                        <tr>
                            <td colSpan={2}>
                                <p>Welcome back <strong>{existingApplication.applicant?.fname||"n/a"}</strong>.</p>
                                <p>
                                    I know you are curious about your application. Thank you for checking in. Your application {existingApplication.review 
                                        ? (<>Was reviewed at <strong>{dayjs(existingApplication.review.at).tz(intelitransit.agency.timeZone).format(intelitransit.timeFormat)}</strong>.</>)
                                        : (<>is still under review! You should be hearing from us shorty.</>)}
                                </p>
                                <br/>
                            </td>
                        </tr>,
                        <tr>
                            <td colSpan={2}>
                                <p>If you need further assistance, you may call us <strong>{intelitransit.agency.name}</strong> at <strong>{intelitransit.agency.phoneNumber}</strong></p>
                            </td>
                        </tr>
                        ) : renderNewApplication() ) 
                }
                </div>
            </div>
        )
    });
}

export default AEVApplication;